import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Modal, SearchBox, Spinner } from "@toss-ads/ui";
import { listAdvertisers } from "../lib/api";

interface Props {
  open: boolean;
  onClose: () => void;
  initiallySelected: string[];
  onConfirm: (advertiserIds: string[]) => Promise<void>;
}

// 유저당 광고주 1개 — 단일 선택.
export function AdvertiserPickerModal({
  open,
  onClose,
  initiallySelected,
  onConfirm,
}: Props) {
  const [selected, setSelected] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const initial = initiallySelected[0] ?? null;

  React.useEffect(() => {
    if (open) {
      setSelected(initial);
      setSearch("");
    }
  }, [open, initial]);

  const { data: advertisers, isLoading } = useQuery({
    queryKey: ["admin", "advertisers"],
    queryFn: listAdvertisers,
    enabled: open,
  });

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = advertisers ?? [];
    if (!q) return rows;
    return rows.filter((a) => `${a.name} ${a.slug}`.toLowerCase().includes(q));
  }, [advertisers, search]);

  const hasChanges = selected !== initial;

  async function handleConfirm() {
    setSubmitting(true);
    try {
      await onConfirm(selected ? [selected] : []);
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  const isEmpty = !isLoading && (advertisers ?? []).length === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="담당 광고주 지정"
      description="이 유저가 담당할 광고주 1개를 선택하세요. 선택 해제하면 미배정이 됩니다."
    >
      <div className="mb-3 flex items-center gap-2">
        <SearchBox
          value={search}
          onChange={setSearch}
          width="100%"
          placeholder="광고주명 / slug 검색"
        />
        <Button
          variant="text"
          size="sm"
          onClick={() => setSelected(null)}
          disabled={selected === null}
        >
          선택 해제
        </Button>
      </div>

      <div className="max-h-[420px] overflow-y-auto rounded-base border border-border">
        {isLoading && (
          <div className="py-10 flex justify-center text-ink-subtle">
            <Spinner size={18} />
          </div>
        )}
        {isEmpty && (
          <div className="py-10 text-center text-[13px] text-ink-subtle">
            등록된 광고주가 없습니다.
          </div>
        )}
        {!isLoading &&
          !isEmpty &&
          filtered.map((a) => (
            <label
              key={a.id}
              className="flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-b-0 cursor-pointer hover:bg-surface-muted"
            >
              <input
                type="radio"
                name="advertiser-pick"
                checked={selected === a.id}
                onChange={() => setSelected(a.id)}
              />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-ink truncate">{a.name}</div>
                <div className="text-[11px] text-ink-subtle truncate font-mono">
                  {a.slug}
                </div>
              </div>
              {!a.active && (
                <span className="text-[10px] text-ink-subtle px-1.5 py-0.5 rounded bg-surface-muted">
                  비활성
                </span>
              )}
            </label>
          ))}
      </div>

      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-border">
        <Button variant="text" size="md" onClick={onClose}>
          취소
        </Button>
        <Button
          variant="primary"
          size="md"
          loading={submitting}
          onClick={() => void handleConfirm()}
          disabled={!hasChanges}
        >
          변경 저장
        </Button>
      </div>
    </Modal>
  );
}
